

import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";

interface PrintableInvoiceProps {
  event: any;
  businessInfo: {
    name: string;
    logoUrl?: string;
    tagline?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstNumber?: string;
  };
}

export default function PrintableInvoice({ event, businessInfo }: PrintableInvoiceProps) {
  const revenue = event.financials?.income_subtotal || event.total_amount || 0;
  const cost = event.financials?.total_cost || 0;
  const profit = event.financials?.profit || 0;
  const paid = event.financials?.customer?.paid || 0;
  const balance = event.financials?.customer?.balance || 0;
  const services = event.services_data || event.financials?.services || [];
  const vendors = event.vendors_data || event.financials?.vendors || [];
  const clientName = event.client?.name || event.name || "—";

  return (
    <div id="print-invoice-container" className="print-container hidden print:block">
      <div className="print-page">
        {/* Header with Gold Accent */}
        <div className="print-header-accent" />
        
        {/* Business Header */}
        <div className="print-header">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              {businessInfo.logoUrl && (
                <img 
                  src={getImageUrl(businessInfo.logoUrl)} 
                  alt="Logo" 
                  className="w-20 h-20 object-contain print-logo"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-stone-900 mb-1 tracking-tight">
                  {businessInfo.name.toUpperCase()}
                </h1>
                <p className="text-sm text-amber-600 font-semibold tracking-wide mb-3">
                  {businessInfo.tagline || "BANQUET HALL & EVENT SERVICES"}
                </p>
                <div className="text-xs text-stone-600 leading-relaxed">
                  {businessInfo.address && <p className="mb-1">{businessInfo.address}</p>}
                  <p>
                    {businessInfo.phone && <span className="font-medium">{businessInfo.phone}</span>}
                    {businessInfo.phone && businessInfo.email && <span className="mx-2">•</span>}
                    {businessInfo.email && <span className="font-medium">{businessInfo.email}</span>}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-right print-invoice-number">
              <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">INVOICE</p>
              <p className="text-2xl font-bold text-amber-600 mb-2">
                INV-{String(event.id).padStart(4, '0')}
              </p>
              <p className="text-xs text-stone-600">
                <span className="font-semibold">Date:</span> {formatDate(event.start_date)}
              </p>
            </div>
          </div>
        </div>

        <div className="print-divider" />

        {/* Event & Client Info Cards */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="print-info-card">
            <p className="print-info-card-label">Client</p>
            <p className="print-info-card-value">{clientName}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Event</p>
            <p className="print-info-card-value">{event.name || "—"}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Guests</p>
            <p className="print-info-card-value">{event.guest_count || "—"}</p>
          </div>
          <div className="print-info-card">
            <p className="print-info-card-label">Status</p>
            <p className="print-info-card-value text-amber-600">{event.status}</p>
          </div>
        </div>

        {/* Combined Services & Vendors Table */}
        {(services.length > 0 || vendors.length > 0) && (
          <div className="mb-4">
            <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">Items</h3>
            </div>
            <table className="print-table">
              <thead>
                <tr>
                  <th className="text-left">Description</th>
                  <th className="text-center w-16">Type</th>
                  <th className="text-center w-16">QTY</th>
                  <th className="text-right w-28">Rate</th>
                  <th className="text-right w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc: any, idx: number) => {
                  const servicePrice = svc.unit_price || svc.selling_price || svc.base_price || svc.price || 0;
                  return (
                    <tr key={`service-${idx}`}>
                      <td className="font-medium text-stone-800">{svc.name || svc.service_name || svc.description || "Service"}</td>
                      <td className="text-center text-xs text-stone-500 uppercase">Service</td>
                      <td className="text-center text-stone-700">{svc.quantity || 1}</td>
                      <td className="text-right text-stone-600">
                        {servicePrice > 0 ? formatCurrencyFull(servicePrice) : '—'}
                      </td>
                      <td className="text-right font-bold text-stone-900">
                        {formatCurrencyFull((svc.quantity || 1) * servicePrice)}
                      </td>
                    </tr>
                  );
                })}
                {vendors.map((vendor: any, idx: number) => {
                  const vendorPrice = vendor.unit_price || vendor.cost || vendor.price || 0;
                  return (
                    <tr key={`vendor-${idx}`}>
                      <td className="font-medium text-stone-800">{vendor.name || vendor.vendor_name || vendor.description || "Vendor"}</td>
                      <td className="text-center text-xs text-stone-500 uppercase">Vendor</td>
                      <td className="text-center text-stone-700">{vendor.quantity || 1}</td>
                      <td className="text-right text-stone-600">
                        {vendorPrice > 0 ? formatCurrencyFull(vendorPrice) : '—'}
                      </td>
                      <td className="text-right font-bold text-stone-900">
                        {formatCurrencyFull((vendor.quantity || 1) * vendorPrice)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Table */}
        <div className="mb-4 mt-4">
          <table className="print-table print-summary-table">
            <tbody>
              <tr className="border-t-2 border-stone-300">
                <td colSpan={4} className="text-right font-semibold text-stone-700">SUBTOTAL</td>
                <td className="text-right font-bold w-28 text-stone-900">{formatCurrencyFull(revenue)}</td>
              </tr>
              <tr className="print-total-row">
                <td colSpan={4} className="text-right font-bold text-lg">TOTAL AMOUNT</td>
                <td className="text-right font-bold w-28 text-lg">{formatCurrencyFull(revenue)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="text-right text-stone-600">Amount Paid</td>
                <td className="text-right w-28 text-emerald-600 font-bold">
                  {formatCurrencyFull(paid)}
                </td>
              </tr>
              <tr className={balance > 0 ? "print-balance-due" : "print-balance-paid"}>
                <td colSpan={4} className="text-right font-bold text-base">Balance Due</td>
                <td className="text-right font-bold w-28 text-base">{formatCurrencyFull(balance)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Payment Status Banner */}
        <div className="print-payment-status mb-8">
          <div>
            <p className="font-bold text-sm mb-1.5 uppercase tracking-wide">Payment Status</p>
            <p className="text-xs text-stone-700">
              <span className="font-semibold">Paid:</span> {formatCurrencyFull(paid)} <span className="text-stone-400">of</span> {formatCurrencyFull(revenue)}
            </p>
          </div>
          <div className="text-right">
            {balance > 0 ? (
              <p className="text-red-600 font-bold text-base">
                Outstanding: {formatCurrencyFull(balance)}
              </p>
            ) : (
              <p className="text-emerald-600 font-bold text-base">✓ FULLY PAID</p>
            )}
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-stone-900 mb-3 uppercase tracking-wide border-b border-stone-300 pb-2">Terms & Conditions</h3>
          <ul className="text-xs text-stone-700 space-y-1.5 leading-relaxed">
            <li>• <span className="font-semibold">Payment Terms:</span> 50% advance required to confirm booking.</li>
            <li>• <span className="font-semibold">Balance Due:</span> 7 days before the event date.</li>
            <li>• <span className="font-semibold">Cancellation Policy:</span> Cancellations made 30+ days before event are refundable.</li>
            <li>• This invoice is electronically generated and valid without signature.</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <div>
            <p className="font-bold text-sm text-stone-900">Thank you for choosing {businessInfo.name}!</p>
            <p className="text-xs text-stone-600 mt-1.5">
              <span className="font-semibold">For queries:</span> {businessInfo.email || "info@shalimar.com"} | {businessInfo.phone || "+91-XXXX-XXXX"}
            </p>
          </div>
          <div className="text-right text-xs text-stone-500">
            <p className="font-semibold text-amber-600">Generated on</p>
            <p className="mt-0.5">{new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
